import React from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { formatTokens, formatCurrency, formatPercent } from '@/utils/formatters';

const MiniChart: React.FC<{ data: number[]; color: string; height?: number }> = ({ data, color, height = 48 }) => {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 200;
  const h = height;
  const stepX = w / (data.length - 1);

  const points = data.map((v, i) => `${i * stepX},${h - ((v - min) / range) * h}`).join(' ');

  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        opacity={0.8}
      />
      {/* Remplissage sous la courbe */}
      <polygon
        points={`0,${h} ${points} ${(data.length - 1) * stepX},${h}`}
        fill={color}
        opacity={0.1}
      />
      {/* Dernier point */}
      <circle
        cx={(data.length - 1) * stepX}
        cy={h - ((data[data.length - 1] - min) / range) * h}
        r="3"
        fill={color}
        opacity={0.9}
      />
    </svg>
  );
};

const BudgetGauge: React.FC<{ used: number; total: number }> = ({ used, total }) => {
  const pct = Math.min(1, used / total);
  const critical = pct > 0.85;
  const warning = pct > 0.65;
  const color = critical ? '#FF1744' : warning ? '#FFB300' : '#00E5FF';

  return (
    <div className="bg-bg-elevated rounded-lg border border-bg-border p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-pixel text-xs text-gray-400">BUDGET TOKENS</span>
        <span className="font-mono text-xs" style={{ color }}>{formatPercent(pct)} utilisé</span>
      </div>
      <div className="h-4 bg-gray-900 rounded-full overflow-hidden mb-2">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct * 100}%`,
            background: critical
              ? 'linear-gradient(90deg, #FF6D00, #FF1744)'
              : warning
              ? 'linear-gradient(90deg, #FFD700, #FF6D00)'
              : 'linear-gradient(90deg, #00E5FF, #2979FF)',
            boxShadow: `0 0 10px ${color}60`,
          }}
        />
      </div>
      <div className="flex justify-between font-mono text-xs text-gray-500">
        <span>{formatTokens(used)} utilisés</span>
        <span>{formatTokens(total - used)} restants</span>
      </div>
      {critical && (
        <div className="mt-2 p-2 rounded bg-neon-red/10 border border-neon-red/30">
          <span className="font-mono text-xs text-neon-red">⚠ Plafond critique — vérifier les règles</span>
        </div>
      )}
    </div>
  );
};

export const EconomyView: React.FC = () => {
  const metrics = useSimulationStore(s => s.metrics);
  const agents = useSimulationStore(s => s.agents);
  const stores = useSimulationStore(s => s.stores);

  const totalCostByAgent = agents.map(a => ({
    agent: a,
    tokens: a.tokensBurned,
    pct: a.tokensBurned / (metrics.tokensUsedToday || 1),
  })).sort((a, b) => b.tokens - a.tokens);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <h1 className="font-pixel text-sm text-gray-200">ÉCONOMIE</h1>

      {/* Budget global */}
      <BudgetGauge used={metrics.tokensUsedToday} total={metrics.totalTokensBudget} />

      {/* KPIs globaux */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-bg-elevated rounded-lg border border-bg-border p-4">
          <div className="font-mono text-xs text-gray-500 mb-1">CA DU JOUR</div>
          <div className="font-mono text-2xl font-bold text-neon-gold" style={{ textShadow: '0 0 10px #FFD70060' }}>
            {formatCurrency(metrics.totalRevenue)}
          </div>
          <MiniChart data={metrics.revenueHistory} color="#FFD700" />
        </div>
        <div className="bg-bg-elevated rounded-lg border border-bg-border p-4">
          <div className="font-mono text-xs text-gray-500 mb-1">MARGE</div>
          <div className="font-mono text-2xl font-bold text-neon-green">
            {formatCurrency(metrics.totalMargin)}
          </div>
          <div className="font-mono text-xs text-gray-600 mt-1">
            {metrics.totalRevenue > 0 ? formatPercent(metrics.totalMargin / metrics.totalRevenue) : '—'} du CA
          </div>
        </div>
        <div className="bg-bg-elevated rounded-lg border border-bg-border p-4">
          <div className="font-mono text-xs text-gray-500 mb-1">TOKENS CONSOMMÉS</div>
          <div className="font-mono text-2xl font-bold text-neon-cyan">
            {formatTokens(metrics.tokensUsedToday)}
          </div>
          <MiniChart data={metrics.tokenHistory} color="#00E5FF" />
        </div>
      </div>

      {/* Dépense par agent */}
      <div className="bg-bg-elevated rounded-lg border border-bg-border p-4">
        <div className="font-pixel text-xs text-gray-400 mb-3">CONSOMMATION PAR AGENT</div>
        <div className="space-y-2">
          {totalCostByAgent.map(({ agent, tokens, pct }) => (
            <div key={agent.id} className="flex items-center gap-3">
              <span className="font-pixel w-16 flex-shrink-0" style={{ color: agent.color, fontSize: '6px' }}>
                {agent.codename}
              </span>
              <div className="flex-1 h-2 bg-gray-900 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct * 100}%`, background: agent.color, boxShadow: `0 0 4px ${agent.color}` }}
                />
              </div>
              <span className="font-mono text-xs text-gray-500 w-14 text-right">{formatTokens(tokens)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Performance par boutique */}
      <div className="bg-bg-elevated rounded-lg border border-bg-border p-4">
        <div className="font-pixel text-xs text-gray-400 mb-3">PERFORMANCE BOUTIQUES</div>
        <div className="space-y-3">
          {stores.map(store => (
            <div key={store.id} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-base">{store.emoji}</span>
                <span className="font-mono text-xs text-gray-300">{store.name}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-neon-gold">{formatCurrency(store.metrics.revenue)}</span>
                <span className="font-mono text-xs text-neon-green">{formatPercent(store.metrics.margin)}</span>
                <span className={`font-mono text-xs ${store.metrics.growth >= 0 ? 'text-neon-green' : 'text-neon-red'}`}>
                  {store.metrics.growth >= 0 ? '▲' : '▼'}{Math.abs(store.metrics.growth).toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
